import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import Select from "react-select";
import { enqueueSnackbar } from "notistack";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  getAllAssetDepartments,
  createAssetDepartment,
  getAssetsByDepartmentStatus,
  exportAssetsByDepartmentStatus,
  bulkUpdateAssetDepartment,
  bulkUpdateInstitutionCategory,
  removeAssetDepartment,
  getAssetCategories,
  getAstInstitutionCategories,
  deleteAsset,
  getAstAssetObservedConditions,
} from "../../services/ValitanApiService";
import { applyReplacementPricing } from "../../services/PricingService";

const parseList = (response) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const parsePagination = (response) =>
  response?.data?.pagination ?? { page: 0, size: 50, totalElements: 0, totalPages: 0 };

const downloadBlob = (response, filename) => {
  const blob = response?.data;
  if (!blob || blob.size === 0) throw new Error("Export file was empty.");
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

const PAGE_SIZE = 50;

const getPricingStatus = (asset) =>
  String(asset?.pricing_status ?? asset?.pricingStatus ?? "").trim().toLowerCase();

const getReplacementCost = (asset) => {
  const value =
    asset?.current_replacement_cost ??
    asset?.original_replacement_cost ??
    asset?.replacement_cost ??
    asset?.currentReplacementCost ??
    asset?.originalReplacementCost ??
    asset?.replacementCost;

  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";

  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    maximumFractionDigits: 0,
  }).format(amount);
};

const InlineAssign = ({ deptOptions, onSave, onCancel, saving }) => {
  const [deptId, setDeptId] = useState(null);
  return (
    <div className="d-flex gap-1 align-items-center" style={{ minWidth: 260 }}>
      <div style={{ flex: 1 }}>
        <Select
          options={deptOptions}
          value={deptOptions.find((d) => d.value === deptId) || null}
          onChange={(opt) => setDeptId(opt?.value || null)}
          placeholder="Select dept…"
          isClearable
          menuPortalTarget={document.body}
          styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }), control: (b) => ({ ...b, minHeight: 30 }) }}
        />
      </div>
      <Button size="sm" color="primary" className="py-0 px-2" disabled={!deptId || saving}
        onClick={() => onSave(deptId)}>
        {saving ? <Spinner size="sm" /> : "Save"}
      </Button>
      <Button size="sm" color="outline-secondary" className="py-0 px-2" onClick={onCancel}>✕</Button>
    </div>
  );
};

const AssetTable = ({ rows, hasDepartment, deptOptions, selectedIds, onToggle, onSelectAll, onDeselectAll, onRowUpdate }) => {
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(String(r.id)));
  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [detailAsset, setDetailAsset] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleSave = async (assetId, departmentId) => {
    setSavingId(assetId);
    try {
      await bulkUpdateAssetDepartment({ assetIds: [assetId], departmentId });
      enqueueSnackbar("Department updated.", { variant: "success" });
      setEditingId(null);
      onRowUpdate();
    } catch {
      enqueueSnackbar("Failed to update department.", { variant: "error" });
    } finally {
      setSavingId(null);
    }
  };

  const handleRemove = async (assetId) => {
    setSavingId(assetId);
    try {
      await removeAssetDepartment({ assetIds: [assetId] });
      enqueueSnackbar("Department removed.", { variant: "success" });
      onRowUpdate();
    } catch {
      enqueueSnackbar("Failed to remove department.", { variant: "error" });
    } finally {
      setSavingId(null);
    }
  };

  // Delete the whole asset with confirmation
  const handleDeleteAsset = async (assetId) => {
    const asset = rows.find((r) => String(r.id) === String(assetId));
    const confirmed = window.confirm(
      `Are you sure you want to delete asset "${asset?.name || "(no name)"}" (Barcode: ${asset?.barcode || "-"})? This action cannot be undone.`
    );
    if (!confirmed) return;
    setSavingId(assetId);
    try {
      await deleteAsset(assetId);
      enqueueSnackbar("Asset deleted.", { variant: "success" });
      onRowUpdate();
    } catch {
      enqueueSnackbar("Failed to delete asset.", { variant: "error" });
    } finally {
      setSavingId(null);
    }
  };

  // Open details modal
  const openDetails = (asset) => {
    setDetailAsset(asset);
    setDetailOpen(true);
  };

  const closeDetails = () => {
    setDetailOpen(false);
    setDetailAsset(null);
  };

  return (
    <>
      <div className="table-responsive">
        <Table bordered hover size="sm" className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: 110 }}>
                <Button size="sm" color={allSelected ? "secondary" : "outline-primary"} className="w-100 py-0"
                  onClick={() => allSelected ? onDeselectAll() : onSelectAll(rows)}>
                  {allSelected ? "Deselect All" : "Select All"}
                </Button>
              </th>
              <th>#</th>
              <th>Barcode</th>
              <th>Name</th>
              <th>Description</th>
              <th>Category</th>
              <th>Institution</th>
              <th>Dept Code</th>
              <th>Department Name</th>
              <th>Building / Floor / Room</th>
              <th>Qty</th>
              <th>Condition</th>
              <th>Pricing Status</th>
              <th>Unit Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={15} className="text-center text-muted py-4">No assets found.</td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const isSelected = selectedIds.has(String(row.id));
                const isEditing = editingId === String(row.id);
                const isSaving = savingId === String(row.id);
                const pricingStatus = getPricingStatus(row);
                const replacementCost = getReplacementCost(row);
                return (
                  <tr key={row.id} className={isSelected ? "table-primary" : ""}>
                    <td className="text-center">
                      <Button size="sm" color={isSelected ? "primary" : "outline-secondary"}
                        className="py-0 px-2" onClick={() => onToggle(String(row.id))}>
                        {isSelected ? "Selected" : "Select"}
                      </Button>
                    </td>
                    <td className="text-muted">{idx + 1}</td>
                    <td><code className="small">{row.barcode || "—"}</code></td>
                    <td>{row.name || "—"}</td>
                    <td><span className="small text-muted">{row.description || "—"}</span></td>
                    <td><Badge color="secondary" className="fw-normal">{row.category_code || "—"}</Badge></td>
                    <td>{row.institution_name || "—"}</td>
                    <td>{row.department_code ? <Badge color="info" className="fw-normal">{row.department_code}</Badge> : <span className="text-muted">—</span>}</td>
                    <td>{row.department_name || <span className="text-muted">—</span>}</td>
                    <td className="small text-muted">{[row.building_name, row.floor, row.room_name_or_number].filter(Boolean).join(" / ") || "—"}</td>
                    <td>{row.quantity || 1}</td>
                    <td>
                      <Badge color="secondary" className="fw-normal">
                        {row.condition || "—"}
                      </Badge>
                    </td>
                    <td>
                      <Badge color={pricingStatus === "priced" ? "success" : "warning"} className="fw-normal">
                        {pricingStatus === "priced" ? "With Pricing" : "Without Pricing"}
                      </Badge>
                    </td>
                    <td>{formatCurrency(replacementCost)}</td>
                    <td>
                      {isEditing ? (
                        <InlineAssign
                          deptOptions={deptOptions}
                          onSave={(deptId) => handleSave(String(row.id), deptId)}
                          onCancel={() => setEditingId(null)}
                          saving={isSaving}
                        />
                      ) : (
                        <div className="d-flex gap-1">
                          <Button size="sm" color="outline-primary" className="py-0 px-2"
                            disabled={isSaving} onClick={() => setEditingId(String(row.id))}>
                            {hasDepartment ? "Change" : "Assign"}
                          </Button>
                          {hasDepartment && (
                            <Button size="sm" color="outline-danger" className="py-0 px-2"
                              disabled={isSaving} onClick={() => handleRemove(String(row.id))}>
                              {isSaving ? <Spinner size="sm" /> : "Remove"}
                            </Button>
                          )}
                          <Button size="sm" color="danger" className="py-0 px-2"
                            disabled={isSaving} onClick={() => handleDeleteAsset(String(row.id))}>
                            {isSaving ? <Spinner size="sm" /> : "Delete"}
                          </Button>
                          <Button size="sm" color="info" className="py-0 px-2"
                            disabled={isSaving} onClick={() => openDetails(row)}>
                            Details
                          </Button>
                          {row.barcode && (
                            <Button size="sm" color="secondary" className="py-0 px-2"
                              disabled={isSaving} onClick={() => openDetails(row)}>
                              Barcodes
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Asset Details Modal */}
      <Modal isOpen={detailOpen} toggle={closeDetails} size="lg">
        <ModalHeader toggle={closeDetails}>Asset Details</ModalHeader>
        <ModalBody>
          {detailAsset && (
            <div>
              <p><strong>ID:</strong> {detailAsset.id}</p>
              <p><strong>Name:</strong> {detailAsset.name}</p>
              <p><strong>Description:</strong> {detailAsset.description || "—"}</p>
              <p><strong>Barcode:</strong> {detailAsset.barcode || "—"}</p>
              <p><strong>Category:</strong> {detailAsset.category_code || "—"}</p>
              <p><strong>Institution:</strong> {detailAsset.institution_name || "—"}</p>
              <p><strong>Ward:</strong> {detailAsset.ward_name || "—"}</p>
              <p><strong>Department:</strong> {detailAsset.department_name || "—"} ({detailAsset.department_code || "—"})</p>
              <p><strong>Location:</strong> {[detailAsset.building_name, detailAsset.floor, detailAsset.room_name_or_number].filter(Boolean).join(" / ") || "—"}</p>
              <p><strong>Quantity:</strong> {detailAsset.quantity || 1}</p>
              <p><strong>Condition:</strong> {detailAsset.condition || "—"}</p>
              <p><strong>Pricing Status:</strong> {getPricingStatus(detailAsset) === "priced" ? "With Pricing" : "Without Pricing"}</p>
              <p><strong>Unit Replacement Cost:</strong> {formatCurrency(getReplacementCost(detailAsset))}</p>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

const Pagination = ({ pagination, onPage }) => {
  const { page, totalPages, totalElements } = pagination;
  if (totalPages <= 1) return null;
  return (
    <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
      <small className="text-muted">{totalElements} total assets</small>
      <div className="d-flex gap-1">
        <Button size="sm" outline color="secondary" disabled={page === 0} onClick={() => onPage(page - 1)}>
          ‹ Prev
        </Button>
        <span className="px-2 py-1 small">Page {page + 1} of {totalPages}</span>
        <Button size="sm" outline color="secondary" disabled={page >= totalPages - 1} onClick={() => onPage(page + 1)}>
          Next ›
        </Button>
      </div>
    </div>
  );
};

const TabPanel = ({ hasDepartment, departments, institutionCategories }) => {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, size: PAGE_SIZE, totalElements: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [categoryCode, setCategoryCode] = useState("");
  const [search, setSearch] = useState("");
  const [institutionFilter, setInstitutionFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assignDeptId, setAssignDeptId] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignInstCategory, setAssignInstCategory] = useState(null);
  const [assignInstCategoryOther, setAssignInstCategoryOther] = useState("");
  const [assigningCategory, setAssigningCategory] = useState(false);
  const [pricingAmount, setPricingAmount] = useState("");
  const [pricingSubmitting, setPricingSubmitting] = useState(false);

  useEffect(() => {
    getAssetCategories(0, 100)
      .then((res) => {
        const list = parseList(res).map((c) => ({
          value: String(c.code ?? c.category_code ?? "").toUpperCase(),
          label: `${String(c.code ?? c.category_code ?? "").toUpperCase()} – ${c.name ?? ""}`,
        })).filter((c) => c.value);
        setCategories([{ value: "", label: "All categories" }, ...list]);
      })
      .catch(() => {});

    getAstAssetObservedConditions()
      .then((res) => {
        const list = parseList(res).map((c) => ({
          value: c.name,
          label: c.name,
        })).filter((c) => c.value);
        setConditions([{ value: "", label: "All conditions" }, ...list]);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const res = await getAssetsByDepartmentStatus({
        hasDepartment,
        categoryCode: categoryCode || undefined,
        search: search || undefined,
        institutionFilter: institutionFilter || undefined,
        condition: conditionFilter || undefined,
        page,
        size: PAGE_SIZE,
      });
      setRows(parseList(res));
      setPagination(parsePagination(res));
      setSelectedIds(new Set());
    } catch {
      enqueueSnackbar("Failed to load assets.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [hasDepartment, categoryCode, search, institutionFilter, conditionFilter]);

  useEffect(() => { load(0); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportAssetsByDepartmentStatus({
        hasDepartment,
        categoryCode: categoryCode || undefined,
        search: search || undefined,
        institutionFilter: institutionFilter || undefined,
        condition: conditionFilter || undefined,
      });
      const suffix = categoryCode ? `_${categoryCode.toLowerCase()}` : "";
      downloadBlob(res, `assets_${hasDepartment ? "with" : "without"}_department${suffix}.csv`);
    } catch {
      enqueueSnackbar("Export failed.", { variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  const toggleId = (id) =>
    setSelectedIds((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const selectAll = (currentRows) =>
    setSelectedIds(new Set(currentRows.map((r) => String(r.id))));

  const deselectAll = () => setSelectedIds(new Set());

  const handleBulkAssign = async () => {
    if (!assignDeptId) { enqueueSnackbar("Select a department first.", { variant: "warning" }); return; }
    if (selectedIds.size === 0) { enqueueSnackbar("Select at least one asset.", { variant: "warning" }); return; }
    setAssigning(true);
    try {
      const res = await bulkUpdateAssetDepartment({ assetIds: [...selectedIds], departmentId: assignDeptId });
      enqueueSnackbar(res?.data?.message ?? `${res?.data?.updated ?? 0} asset(s) updated.`, { variant: "success" });
      setSelectedIds(new Set());
      setAssignDeptId(null);
      load(pagination.page);
    } catch {
      enqueueSnackbar("Failed to assign department.", { variant: "error" });
    } finally {
      setAssigning(false);
    }
  };

  const handleBulkAssignCategory = async () => {
    if (!assignInstCategory) { enqueueSnackbar("Select a category first.", { variant: "warning" }); return; }
    if (selectedIds.size === 0) { enqueueSnackbar("Select at least one asset.", { variant: "warning" }); return; }
    setAssigningCategory(true);
    try {
      const isOther = ["OTHER", "OTHERS"].includes(String(assignInstCategory).toUpperCase());
      const res = await bulkUpdateInstitutionCategory({ 
        assetIds: [...selectedIds], 
        institutionCategory: assignInstCategory,
        institutionCategoryOther: isOther ? assignInstCategoryOther : "" 
      });
      enqueueSnackbar(res?.data?.message ?? `${res?.data?.updated ?? 0} asset(s) updated.`, { variant: "success" });
      setSelectedIds(new Set());
      setAssignInstCategory(null);
      setAssignInstCategoryOther("");
      load(pagination.page);
    } catch {
      enqueueSnackbar("Failed to update institution category.", { variant: "error" });
    } finally {
      setAssigningCategory(false);
    }
  };

  const handleBulkPricing = async () => {
    if (selectedIds.size === 0) {
      enqueueSnackbar("Select at least one asset.", { variant: "warning" });
      return;
    }

    const amount = Number(String(pricingAmount).replace(/[^\d]/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) {
      enqueueSnackbar("Enter a valid unit replacement cost.", { variant: "warning" });
      return;
    }

    const confirmed = window.confirm(
      `Apply TZS ${amount.toLocaleString()} to ${selectedIds.size} selected asset(s)?`
    );
    if (!confirmed) return;

    setPricingSubmitting(true);
    try {
      const res = await applyReplacementPricing({
        assetIds: [...selectedIds],
        unitCurrentReplacementCost: amount,
      });
      const updatedCount = res?.data?.data?.updated_count ?? selectedIds.size;
      enqueueSnackbar(`Pricing applied to ${updatedCount} asset(s).`, { variant: "success" });
      setPricingAmount("");
      load(pagination.page);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to apply pricing.",
        { variant: "error" }
      );
    } finally {
      setPricingSubmitting(false);
    }
  };

  const deptOptions = departments.map((d) => ({
    value: d.id,
    label: `${d.code ?? ""} – ${d.name ?? ""}`,
  }));

  const instCategoryOptions = institutionCategories.map((c) => ({
    value: c.name,
    label: c.name,
  }));

  return (
    <div>
      {/* Toolbar */}
      <div className="d-flex align-items-end gap-3 flex-wrap mb-3">
        <div style={{ minWidth: 200 }}>
          <small className="text-muted d-block mb-1">Filter by category</small>
          <Select
            options={categories}
            value={categories.find((c) => c.value === categoryCode) || categories[0] || null}
            onChange={(opt) => setCategoryCode(opt?.value || "")}
            placeholder="All categories"
            isClearable={false}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
          />
        </div>

        <div style={{ minWidth: 180 }}>
          <small className="text-muted d-block mb-1">Search asset</small>
          <input type="text" className="form-control form-control-sm" placeholder="Name, description or barcode"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div style={{ minWidth: 200 }}>
          <small className="text-muted d-block mb-1">Filter by institution</small>
          <input type="text" className="form-control form-control-sm" placeholder="Institution name"
            value={institutionFilter} onChange={(e) => setInstitutionFilter(e.target.value)} />
        </div>

        <div style={{ minWidth: 200 }}>
          <small className="text-muted d-block mb-1">Filter by condition</small>
          <Select
            options={conditions}
            value={conditions.find((c) => c.value === conditionFilter) || conditions[0] || null}
            onChange={(opt) => setConditionFilter(opt?.value || "")}
            placeholder="All conditions"
            isClearable={false}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
          />
        </div>

        <Button color="outline-secondary" size="sm" onClick={() => load(0)} disabled={loading}>
          {loading ? <Spinner size="sm" /> : <i className="ri-refresh-line" />}
          {" "}Refresh
        </Button>

        <Button color="success" size="sm" onClick={handleExport} disabled={exporting}>
          {exporting ? <Spinner size="sm" className="me-1" /> : <i className="ri-download-2-line me-1" />}
          Download CSV
        </Button>

        {!hasDepartment && selectedIds.size > 0 && (
          <>
            <div style={{ minWidth: 260 }}>
              <small className="text-muted d-block mb-1">Assign department to {selectedIds.size} selected</small>
              <Select
                options={deptOptions}
                value={deptOptions.find((d) => d.value === assignDeptId) || null}
                onChange={(opt) => setAssignDeptId(opt?.value || null)}
                placeholder="Select department…"
                isClearable
                menuPortalTarget={document.body}
                styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
              />
            </div>
            <Button color="primary" size="sm" onClick={handleBulkAssign}
              disabled={assigning || !assignDeptId} style={{ alignSelf: "flex-end" }}>
              {assigning ? <Spinner size="sm" className="me-1" /> : <i className="ri-check-line me-1" />}
              Assign
            </Button>
            <Button color="outline-secondary" size="sm" onClick={deselectAll} style={{ alignSelf: "flex-end" }}>
              Clear
            </Button>
          </>
        )}

        {selectedIds.size > 0 && (
          <div className="d-flex align-items-end gap-2 flex-wrap ms-auto p-2 bg-light rounded border border-light">
            <div style={{ minWidth: 190 }}>
              <small className="text-muted d-block mb-1">Unit Price (TZS) for {selectedIds.size}</small>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Enter amount"
                value={pricingAmount}
                onChange={(e) => setPricingAmount(String(e.target.value).replace(/[^\d]/g, ""))}
              />
            </div>
            <Button color="primary" size="sm" onClick={handleBulkPricing}
              disabled={pricingSubmitting || !pricingAmount} style={{ alignSelf: "flex-end" }}>
              {pricingSubmitting ? <Spinner size="sm" className="me-1" /> : <i className="ri-money-dollar-circle-line me-1" />}
              Apply Pricing
            </Button>
            <div style={{ minWidth: 200 }}>
              <small className="text-muted d-block mb-1">Update Inst. Category ({selectedIds.size})</small>
              <Select
                options={instCategoryOptions}
                value={instCategoryOptions.find((c) => c.value === assignInstCategory) || null}
                onChange={(opt) => setAssignInstCategory(opt?.value || null)}
                placeholder="Select category…"
                isClearable
                menuPortalTarget={document.body}
                styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
              />
            </div>
            {["OTHER", "OTHERS"].includes(String(assignInstCategory).toUpperCase()) && (
              <div style={{ minWidth: 150 }}>
                <small className="text-muted d-block mb-1">Specify Other</small>
                <input type="text" className="form-control form-control-sm"
                  value={assignInstCategoryOther} onChange={(e) => setAssignInstCategoryOther(e.target.value)} />
              </div>
            )}
            <Button color="success" size="sm" onClick={handleBulkAssignCategory}
              disabled={assigningCategory || !assignInstCategory} style={{ alignSelf: "flex-end" }}>
              {assigningCategory ? <Spinner size="sm" className="me-1" /> : <i className="ri-check-line me-1" />}
              Update
            </Button>
          </div>
        )}
      </div>

      <AssetTable
        rows={rows}
        hasDepartment={hasDepartment}
        deptOptions={deptOptions}
        selectedIds={selectedIds}
        onToggle={toggleId}
        onSelectAll={selectAll}
        onDeselectAll={deselectAll}
        onRowUpdate={() => load(pagination.page)}
      />
      <Pagination pagination={pagination} onPage={(p) => load(p)} />
    </div>
  );
};

const DepartmentManagement = () => {
  document.title = "Department Management | Valitan";

  const [activeTab, setActiveTab] = useState("without");
  const [departments, setDepartments] = useState([]);
  const [showAddDept, setShowAddDept] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptCode, setNewDeptCode] = useState('');
  const [institutionCategories, setInstitutionCategories] = useState([]);

  useEffect(() => {
    getAllAssetDepartments(0, 200)
      .then((res) => setDepartments(parseList(res)))
      .catch(() => {});
    // Refresh departments after adding
    const refreshDepartments = () => {
      getAllAssetDepartments(0, 200)
        .then((res) => setDepartments(parseList(res)))
        .catch(() => {});
    };
    getAstInstitutionCategories()
      .then((res) => setInstitutionCategories(parseList(res)))
      .catch(() => {});
    // Expose refresh function for modal submit
    window.refreshDepartments = refreshDepartments;
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Asset Valuation" breadcrumbItem="Department Management" />

        <Row>
          <Col>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <h4 className="card-title mb-1">Asset Department Assignment</h4>
                    <p className="text-muted mb-0">
                      View, assign, change, or remove departments. Download filtered exports.
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-3">
                  <Button color={activeTab === "without" ? "primary" : "outline-secondary"} size="sm"
                    onClick={() => setActiveTab("without")}>
                    <i className="ri-error-warning-line me-1" />Without Department
                  </Button>
                  <Button color={activeTab === "with" ? "primary" : "outline-secondary"} size="sm"
                    onClick={() => setActiveTab("with")}>
                    <i className="ri-building-4-line me-1" />With Department
                  </Button>
                  <Button color="success" size="sm" className="ms-2" onClick={() => setShowAddDept(true)}>
                    <i className="ri-add-circle-line me-1" />Add Department
                  </Button>
                </div>
              </CardHeader>

              <CardBody>
                {activeTab === "without" && (
                  <>
                    <Alert color="warning" className="mb-3 py-2">
                      Assets with no department. Use Select + Assign to bulk-assign, or use the Assign button per row.
                    </Alert>
                    <TabPanel key="without" hasDepartment={false} departments={departments} institutionCategories={institutionCategories} />
                  </>
                )}
                {activeTab === "with" && (
                  <>
                    <Alert color="info" className="mb-3 py-2">
                      Assets with a department assigned. Use Change to update or Remove to clear.
                    </Alert>
                    <TabPanel key="with" hasDepartment={true} departments={departments} institutionCategories={institutionCategories} />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        {/* Add Department Modal */}
        <Modal isOpen={showAddDept} toggle={() => setShowAddDept(!showAddDept)}>
          <ModalHeader toggle={() => setShowAddDept(!showAddDept)}>Add New Department</ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label className="form-label">Name</label>
              <input type="text" className="form-control" value={newDeptName} onChange={e => setNewDeptName(e.target.value)} />
            </div>
            <div className="mb-3">
              <label className="form-label">Code</label>
              <input type="text" className="form-control" value={newDeptCode} onChange={e => setNewDeptCode(e.target.value)} />
            </div>
            <Button color="primary" onClick={async () => {
              try {
                await createAssetDepartment({ name: newDeptName, code: newDeptCode });
                enqueueSnackbar('Department created successfully', { variant: 'success' });
                setShowAddDept(false);
                setNewDeptName('');
                setNewDeptCode('');
                // Refresh list
                if (typeof window.refreshDepartments === 'function') window.refreshDepartments();
              } catch (e) {
                enqueueSnackbar('Failed to create department', { variant: 'error' });
              }
            }}>Create</Button>{' '}
            <Button color="secondary" onClick={() => setShowAddDept(false)}>Cancel</Button>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default DepartmentManagement;
